import type { StorageFile, StorageReadDisposition } from "@gencow/core";
import { verifyStorageReadGrant } from "./storage-read-grant.js";
import type { StorageCacheScope } from "./storage-visibility.js";
import { isPublicStorageFile } from "./storage-visibility.js";

export type StorageRouteAccessOptions = {
  appName?: string;
  readGrantSecret?: string;
  requireReadGrant?: boolean;
  nowMs?: () => number;
};

export type StorageRouteIdentity = {
  id?: string;
  sessionId?: string | null;
};

export type StorageReadDecision =
  | {
      ok: true;
      disposition: StorageReadDisposition;
      cacheScope: StorageCacheScope;
      cacheMaxAgeSeconds?: number;
    }
  | { ok: false; status: 403 | 404 | 500; error: string };

export function decideStorageRouteReadAccess(input: {
  storageId: string;
  meta: StorageFile | null;
  grant?: string | null;
  identity?: StorageRouteIdentity | null;
  accessOptions?: StorageRouteAccessOptions;
}): StorageReadDecision {
  if (input.meta && isPublicStorageFile(input.meta)) {
    return { ok: true, disposition: "inline", cacheScope: "public" };
  }

  const grant = input.grant;
  if (!input.accessOptions?.requireReadGrant && !grant) {
    if (!input.meta) return { ok: false, status: 404, error: "Not found" };
    return { ok: true, disposition: "inline", cacheScope: "private" };
  }

  if (!input.accessOptions?.appName || !input.accessOptions?.readGrantSecret) {
    return { ok: false, status: 500, error: "Storage read grant verification is not configured" };
  }

  const nowMs = input.accessOptions.nowMs?.() ?? Date.now();
  const verified = verifyStorageReadGrant({
    token: grant,
    appName: input.accessOptions.appName,
    storageId: input.storageId,
    secret: input.accessOptions.readGrantSecret,
    nowMs,
  });
  if (!verified.ok) {
    return { ok: false, status: 403, error: "Storage read grant denied" };
  }
  if (verified.payload.subject && input.identity?.id !== verified.payload.subject) {
    return { ok: false, status: 403, error: "Storage read grant denied" };
  }
  if (verified.payload.sessionId && input.identity?.sessionId !== verified.payload.sessionId) {
    return { ok: false, status: 403, error: "Storage read grant denied" };
  }
  if (!input.meta) return { ok: false, status: 404, error: "Not found" };
  const remainingSeconds = Math.max(0, Math.floor((verified.payload.exp - nowMs) / 1000));
  return {
    ok: true,
    disposition: verified.payload.disposition,
    cacheScope: "private",
    cacheMaxAgeSeconds: remainingSeconds,
  };
}
